import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, FlaskConical, Wrench, GraduationCap, ShieldCheck, Building2, MoveRight, Globe, Truck, Package, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import useIsMobile from '../hooks/useIsMobile';

const processSteps = [
  { icon: ClipboardList, title: 'Site Assessment & Planning', path: '/services/consulting' },
  { icon: FlaskConical, title: 'Equipment Trials & On-Site Demos', path: '/services/demos' },
  { icon: Wrench, title: 'Equipment Installation & Integration', path: '/services/installation' },
  { icon: GraduationCap, title: 'Training & Staff Education', path: '/services/training' },
  { icon: ShieldCheck, title: 'Ongoing Support & Maintenance', path: '/services/maintenance' },
];

const serviceGroups = [
  {
    group: 'Planning & Assessment',
    items: [
      {
        icon: ClipboardList,
        title: 'Facility Assessment & Project Planning',
        body: 'We begin every project with a comprehensive evaluation of your space, workflow, and infrastructure. Our team collaborates with construction, IT, facilities, and clinical stakeholders to ensure all mounting locations, power, data, and structural requirements are identified early — supporting smooth installs in both active facilities and new construction projects.',
      },
      {
        icon: FlaskConical,
        title: 'Equipment Trials & On-Site Demos',
        body: 'To support confident decision-making, we provide on-site trials by installing select demo arms and mounting solutions. This hands-on approach allows clinical teams to validate ergonomics, reach, workflow compatibility, and usability in real-world environments before final equipment selection.',
      },
      {
        icon: ShieldCheck,
        title: 'ADA & Joint Commission Compliance',
        body: 'Every installation we perform is evaluated against ADA accessibility requirements and Joint Commission (JCO) standards. We ensure mounting heights, reach ranges, clearances, and equipment positioning meet compliance requirements — so your facility is inspection-ready from day one.',
      },
    ],
  },
  {
    group: 'Installation & Integration',
    items: [
      {
        icon: Wrench,
        title: 'Medical Equipment Installation',
        body: 'Our certified technicians manage complete installation services for articulating arms, patient monitors, mobile carts, workstations, and track-mounted systems. From single rooms to full department rollouts, we deliver precise, code-compliant installations with minimal disruption to patient care.',
      },
      {
        icon: Building2,
        title: 'Custom Installation Solutions',
        body: 'JVP designs and delivers custom equipment installations tailored to your facility\'s unique layout, workflow, and clinical requirements. From non-standard room configurations to specialized mounting solutions, we work closely with your team to ensure every installation fits the space and supports the way your staff works.',
      },
      {
        icon: MoveRight,
        title: 'Equipment Configuration & Room Integration',
        body: 'Beyond installation, we configure equipment to support efficient clinical workflows. This includes positioning, arm reach optimization, cable routing, and complete room setups designed to improve accessibility, reduce clutter, and enhance patient and caregiver experience.',
      },
    ],
  },
  {
    group: 'Maintenance & Support',
    items: [
      {
        icon: ShieldCheck,
        title: 'Preventive Maintenance & Service Support',
        body: 'JVP provides ongoing maintenance and service support to keep equipment functioning safely and reliably. Our preventive maintenance services help extend equipment life, reduce downtime, and identify issues before they impact clinical operations.',
      },
      {
        icon: Truck,
        title: 'Fleet Maintenance for Mobile Workstations',
        body: 'We offer dedicated fleet maintenance programs for mobile workstation carts — keeping your entire fleet operational, safe, and compliant. Services include scheduled inspections, battery health checks, wheel and brake servicing, and hardware repairs to minimize downtime across your unit.',
      },
      {
        icon: Package,
        title: 'Parts & Replacement Components',
        body: 'Through our solutions provider network, JVP sources and supplies genuine replacement parts for the equipment we install and service. From mounting hardware and arm components to cart accessories and power systems, we keep your equipment running with the right parts.',
      },
      {
        icon: MoveRight,
        title: 'Equipment Relocation & Reconfiguration',
        body: 'As departments evolve, we support equipment moves, reconfigurations, and upgrades. Whether expanding a unit or repurposing existing space, our team ensures equipment is safely relocated and optimized for the new environment.',
      },
    ],
  },
  {
    group: 'Enterprise & Multi-Site',
    items: [
      {
        icon: Globe,
        title: 'National Deployment & Multi-Site Rollouts',
        body: 'With experience supporting healthcare facilities nationwide, JVP manages consistent, repeatable installations across multiple locations. We standardize processes, coordinate scheduling, and maintain quality across large-scale deployments and system-wide rollouts.',
      },
    ],
  },
];

const AccordionItem: React.FC<{ icon: React.ElementType; title: string; body: string }> = ({ icon: Icon, title, body }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '1rem' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(147,197,253,0.1)', borderRadius: '8px', padding: '0.6rem', flexShrink: 0 }}>
            <Icon size={18} color="var(--color-primary-pale)" strokeWidth={2} />
          </div>
          <span style={{ fontWeight: 600, color: 'var(--color-surface-alt)', fontSize: '1rem' }}>{title}</span>
        </div>
        <ChevronDown size={18} color="var(--color-text-muted-strong)" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <p style={{ color: 'var(--color-muted-on-dark-light)', lineHeight: 1.8, fontSize: '0.95rem', paddingBottom: '1.25rem', margin: 0, paddingLeft: '3rem' }}>
          {body}
        </p>
      )}
    </div>
  );
};

const ServicesPage: React.FC = () => {
  const isMobile = useIsMobile();
  return (
    <>
      <SEO
        title="Healthcare Equipment Installation Services"
        description="End-to-end healthcare equipment services — site assessment, equipment trials, installation, staff training, and ongoing maintenance nationwide."
        keywords="healthcare equipment installation services, hospital site assessment, medical equipment trials, clinical staff training, equipment maintenance, healthcare facility planning"
        url="https://www.jvphealthtechsolutions.com/services"
      />
      <div className="page" style={{ paddingTop: '80px' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', textAlign: 'center' }}>
          <h1 style={{ color: 'white', fontSize: isMobile ? '2rem' : '2.8rem', marginBottom: '1rem' }}>How We Work</h1>
          <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', maxWidth: '640px', margin: '0 auto', opacity: 0.9, lineHeight: 1.7 }}>
            From the first site visit to long-term support, we handle every step with white-glove care — so you never have to worry about what comes next.
          </p>
        </div>

        {/* Process steps */}
        <div style={{ background: 'var(--color-surface)', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <h2 style={{ textAlign: 'center', color: 'var(--color-text)', fontSize: isMobile ? '1.5rem' : '2rem', marginBottom: '0.5rem' }}>Our Process</h2>
            <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', marginBottom: '3.5rem' }}>Every engagement follows the same proven path — from first visit to long-term support.</p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-start', justifyContent: 'center', gap: isMobile ? '0' : '0' }}>
              {processSteps.map((s, i) => (
                <div key={s.path} style={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
                  <Link to={s.path} style={{
                    textDecoration: 'none',
                    width: isMobile ? '100%' : '210px',
                    textAlign: isMobile ? 'left' : 'center',
                    padding: isMobile ? '1rem 0' : '0 12px',
                    display: isMobile ? 'flex' : 'inline-block',
                    alignItems: 'center',
                    gap: '1rem',
                    borderBottom: isMobile && i < processSteps.length - 1 ? '1px solid var(--color-border)' : 'none',
                    transition: 'transform 0.3s',
                  }}
                    onMouseEnter={e => !isMobile && (e.currentTarget.style.transform = 'translateY(-5px)')}
                    onMouseLeave={e => !isMobile && (e.currentTarget.style.transform = 'translateY(0)')}>
                    <div style={{ width: isMobile ? '48px' : '64px', height: isMobile ? '48px' : '64px', borderRadius: '50%', background: 'var(--color-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: isMobile ? '0' : '0 auto 1rem', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', flexShrink: 0 }}>
                      <s.icon size={isMobile ? 22 : 28} color="white" strokeWidth={1.5} />
                    </div>
                    {isMobile ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.08em', flexShrink: 0 }}>STEP {i + 1}</span>
                        <span style={{ fontWeight: 700, color: 'var(--color-text)', fontSize: '0.95rem' }}>{s.title}</span>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>STEP {i + 1}</div>
                        <div style={{ fontWeight: 700, color: 'var(--color-text)', fontSize: '0.95rem', lineHeight: 1.4, minHeight: '2.8em' }}>{s.title}</div>
                      </>
                    )}
                  </Link>
                  {i < processSteps.length - 1 && !isMobile && (
                    <div style={{ paddingTop: '28px', color: 'var(--color-muted-on-dark-light)', fontSize: '1.5rem', flexShrink: 0 }}>›</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Services accordion */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy-deep) 0%, var(--color-navy) 100%)', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem' }}>
          <div style={{ maxWidth: '860px', margin: '0 auto' }}>
            <h2 style={{ textAlign: 'center', color: 'var(--color-white)', fontSize: isMobile ? '1.5rem' : '2rem', marginBottom: '0.5rem' }}>Our Services</h2>
            <p style={{ textAlign: 'center', color: 'var(--color-muted-on-dark-light)', marginBottom: '3.5rem' }}>Comprehensive solutions for every stage of your equipment lifecycle.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {serviceGroups.map(group => (
                <div key={group.group} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', overflow: 'hidden' }}>
                  <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', textAlign: 'center' }}>
                    <p style={{ color: 'var(--color-primary-pale)', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>{group.group}</p>
                  </div>
                  <div style={{ padding: '0 1.5rem' }}>
                    {group.items.map(item => (
                      <AccordionItem key={item.title} icon={item.icon} title={item.title} body={item.body} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* CTA */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: '5rem 2rem', textAlign: 'center' }}>
          <h2 style={{ color: 'white', fontSize: '2.2rem', marginBottom: '1rem' }}>Ready to start the perfect service experience?</h2>
          <p style={{ opacity: 0.9, fontSize: '1.1rem', marginBottom: '2rem', maxWidth: '500px', margin: '0 auto 2rem' }}>
            Let's begin with a site assessment and build a plan around your facility's needs.
          </p>
          <Link to="/contact" style={{ background: 'white', color: 'var(--color-primary)', padding: '1rem 2.5rem', borderRadius: '50px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', display: 'inline-block' }}>
            Get in Touch
          </Link>
        </div>

      </div>
    </>
  );
};

export default ServicesPage;
